import { Link } from "@tanstack/react-router";
import { ArrowRight, BookOpen, MapPin, Wrench } from "lucide-react";
import type { LinkItem } from "@/lib/internalLinks";

type Group = {
  heading: string;
  intro?: string;
  icon: "guide" | "service" | "area";
  links: LinkItem[];
};

const ICONS = {
  guide: BookOpen,
  service: Wrench,
  area: MapPin,
} as const;

/**
 * Contextual internal-link block. Renders descriptive, keyword-bearing anchor
 * text pointing between blog guides, service pages, and suburb landing pages.
 */
export function ContextualLinks({
  heading = "Related guides, services & service areas",
  intro,
  groups,
}: {
  heading?: string;
  intro?: string;
  groups: Group[];
}) {
  const active = groups.filter((g) => g.links.length > 0);
  if (active.length === 0) return null;

  return (
    <section aria-labelledby="contextual-links" className="border-t border-border bg-card/20">
      <div className="container mx-auto px-6 py-16">
        <h2 id="contextual-links" className="font-display text-2xl md:text-3xl font-bold">
          {heading}
        </h2>
        {intro && <p className="mt-3 max-w-2xl text-muted-foreground">{intro}</p>}

        <div className="mt-8 grid gap-8 md:grid-cols-3">
          {active.map((group) => {
            const Icon = ICONS[group.icon];
            return (
              <div key={group.heading}>
                <h3 className="flex items-center gap-2 font-display text-lg font-semibold">
                  <Icon className="h-4 w-4 text-brand-text" aria-hidden="true" />
                  {group.heading}
                </h3>
                {group.intro && (
                  <p className="mt-2 text-sm text-muted-foreground">{group.intro}</p>
                )}
                <ul className="mt-4 space-y-4">
                  {group.links.map((l) => (
                    <li key={l.to}>
                      <Link
                        to={l.to as never}
                        className="group inline-flex items-start gap-1.5 font-medium text-brand-text underline-offset-4 hover:underline"
                      >
                        <span className="first-letter:uppercase">{l.anchor}</span>
                        <ArrowRight
                          className="mt-1 h-3.5 w-3.5 shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
                          aria-hidden="true"
                        />
                      </Link>
                      <p className="mt-1 text-sm text-muted-foreground">{l.blurb}</p>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
